import type { components, operations } from '@/api/schema'
import type { PurchaseOrderActionReopen } from '@/features/purchase-orders/action-audit-model'
import { formatMoney, formatNumber, formatStatus, parseUTCDate } from '@/lib/utils'

export type ManufacturerDataAgent = components['schemas']['ManufacturerDataAgentRead']
export type LeadTimeCheckStatus = components['schemas']['LeadTimeCheckStatusRead']
export type ManufacturerDataAction = components['schemas']['ManufacturerDataActionRead']
export type ManufacturerDataChange = components['schemas']['ManufacturerDataChangeRead']
export type ManufacturerDataChangeItem = components['schemas']['ManufacturerDataChangeItemRead']
export type ManufacturerDataActionGroup = components['schemas']['ManufacturerDataActionGroupRead']
export type ManufacturerDataActionPage = components['schemas']['ActionGroupsResponse_ManufacturerDataActionGroupRead_']
export type ManufacturerDataActionsParams = NonNullable<
  operations['list_manufacturer_data_actions_dashboard_manufacturer_data_actions_get']['parameters']['query']
>

type PurchaseOrderAction = NonNullable<components['schemas']['PurchaseOrderDetailRead']['actions']>[number]

/**
 * A manufacturer data item as the PO action cards and email views read one (USA-1474): the same fields a
 * PO action has, with the data item's own. In progress counts as open, as it does on a PO.
 */
export function toPurchaseOrderAction(action: ManufacturerDataAction): PurchaseOrderAction {
  return {
    id: action.id,
    uuid: action.uuid,
    purchase_order_id: 0,
    line_item_id: null,
    communication_message_id: action.communication_message_id ?? null,
    action_type: action.action_type,
    status: action.status === 'in_progress' ? 'open' : action.status,
    summary: action.summary,
    details: action.details ?? null,
    structured_data: action.structured_data ?? null,
    expected_date: action.expected_date ?? null,
    charge_amount: null,
    assigned_role: action.assigned_role,
    created_by: action.created_by ?? null,
    resolved_by: action.resolved_by ?? null,
    resolved_at: action.resolved_at ?? null,
    resolution_type: action.resolution_type ?? null,
    resolution_data: action.resolution_data ?? null,
    communicated_at: null,
    customer_approval_required: false,
    customer_approval_status: null,
    customer_communication_thread_id: null,
    shipping_communication_thread_id: null,
    customer_draft_subject: null,
    customer_draft_email: null,
    draft_response: action.draft_response ?? null,
    customer_decision: null,
    customer_decision_details: null,
    customer_decision_at: null,
    created_at: action.created_at,
    updated_at: action.updated_at,
    card: action.card ?? null,
    progress: action.progress ?? [],
  }
}

/** The data items' reopenings in the PO shape the audit lines read; data items are never "communicated". */
export function toPurchaseOrderReopens(reopens: ManufacturerDataAgent['action_reopens']): PurchaseOrderActionReopen[] {
  return (reopens ?? []).map((reopen) => ({ ...reopen, previous_communicated_at: null }))
}

const CENTRAL = 'America/Chicago'

/** "Mon Nov 2, 9:00 am CT": the send time in Central, where the check is scheduled. */
export function formatCentralSendTime(naiveUtc: string): string {
  const at = parseUTCDate(naiveUtc)
  const day = new Intl.DateTimeFormat('en-US', {
    timeZone: CENTRAL,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
    .format(at)
    .replace(',', '')
  const time = new Intl.DateTimeFormat('en-US', { timeZone: CENTRAL, hour: 'numeric', minute: '2-digit' })
    .format(at)
    .replace(/\s?AM$/, ' am')
    .replace(/\s?PM$/, ' pm')
  return `${day}, ${time} CT`
}

/** "Oct 26" (with the year when not this year), for compact date columns. */
export function shortDay(dateStr: string): string {
  const at = parseUTCDate(dateStr)
  return at.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(at.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
  })
}

/** What the Emails tab header says about the next weekly check. */
export type LeadTimeCheckLine =
  | { state: 'scheduled'; when: string; to: string[]; cc: string[] }
  | { state: 'blocked'; reason: string }
  | { state: 'off'; reason: string | null }

export function leadTimeCheckLine(
  status: LeadTimeCheckStatus,
  actorName?: (userId: string | null | undefined) => string | null,
): LeadTimeCheckLine {
  if (!status.enabled) {
    const by = actorName?.(status.switched_by) ?? null
    return {
      state: 'off',
      // Staff turned it off, or it is off by default: nothing bought from them in a year.
      reason: status.explicit === false ? (by ? `Turned off by ${by}` : 'Turned off') : 'No orders in the last 12 months',
    }
  }
  if (status.blocked_reason || !status.next_send_at) {
    return { state: 'blocked', reason: status.blocked_reason || 'No one to email' }
  }
  return {
    state: 'scheduled',
    when: formatCentralSendTime(status.next_send_at),
    to: status.recipients.to,
    cc: status.recipients.cc,
  }
}

function plural(count: number, word: string) {
  return `${count} ${word}${count === 1 ? '' : 's'}`
}

function days(value: string | null | undefined): string | null {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? plural(n, 'business day') : value
}

/** What a change applied to: the whole line, one category, or one product. */
export function changeScope(target: ManufacturerDataChange['target']): string | null {
  const level = target.level
  if (level === 'category' && typeof target.category_name === 'string') return target.category_name
  if (level === 'product' && typeof target.model === 'string') return target.model
  if (level === 'product_line' && typeof target.query === 'string') {
    return typeof target.count === 'number' ? `${target.query} (${target.count} products)` : target.query
  }
  return null
}

/**
 * Changes that write their own summary: "Price sheet: 412 prices set, 38 already right, 11 not in catalog",
 * "Weight and size updated on 12 products; 2 models not found".
 */
const SUMMARIZED = new Set([
  'discount',
  'price_sheet',
  'special',
  'note',
  'product_facts',
  'product_files',
  'new_products',
  'warehouses',
  'warranty',
  // Approved from the weekly order review (USA-1513): "Broken-case fee: No broken-case fee → $10 per broken case".
  'terms',
  'contact',
])

const FALLBACK_LABELS: Record<string, string> = {
  price_sheet: 'Price sheet',
  special: 'Special',
  note: 'List price notice',
  product_facts: 'Weight and size updated',
  product_files: 'Photos and spec sheets added',
  new_products: 'New products added',
  warehouses: 'Warehouses updated',
  warranty: 'Warranty updated',
}

/** "Lead time 10 → 15 business days", "Lead times confirmed": one change in a few words. */
export function describeChange(change: ManufacturerDataChange): string {
  if (change.change_type === 'lead_time_confirmed') return 'Lead times confirmed'
  if (change.change_type === 'lead_time') {
    const next = days(change.new_value) ?? 'not set'
    const previous = days(change.previous_value)
    return previous ? `Lead time ${previous.replace(/ business days?$/, '')} → ${next}` : `Lead time set to ${next}`
  }
  if (change.summary && SUMMARIZED.has(change.change_type)) return change.summary
  const fallback = FALLBACK_LABELS[change.change_type]
  if (fallback) return change.new_value ? `${fallback}: ${change.new_value}` : fallback
  const label = formatStatus(change.change_type)
  if (change.new_value == null) return label
  return change.previous_value != null
    ? `${label} ${change.previous_value} → ${change.new_value}`
    : `${label} set to ${change.new_value}`
}

/**
 * Applied pricing and product data can be put back from here; a lead time is changed back on the manufacturer's
 * terms.
 */
const REVERTIBLE = new Set([
  'discount',
  'price_sheet',
  'special',
  'product_facts',
  'product_files',
  'new_products',
  'warehouses',
  'terms',
  'contact',
])

export function canRevert(change: ManufacturerDataChange): boolean {
  return change.status === 'applied' && REVERTIBLE.has(change.change_type)
}

/** "Jan 1" (with the year when not this year) from a date-only "2027-01-01", read as that calendar day. */
export function calendarDay(isoDate: string): string {
  const [year, month, day] = isoDate.slice(0, 10).split('-').map(Number)
  if (!year || !month || !day) return isoDate
  const at = new Date(year, month - 1, day)
  return at.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(year === new Date().getFullYear() ? {} : { year: 'numeric' }),
  })
}

/** The chip a change wears when it is not simply applied: when it takes effect, held for staff, put back. */
export type ChangeStatusChip =
  | { state: 'scheduled'; label: string }
  | { state: 'held'; label: string }
  | { state: 'reverted'; label: string }
  | { state: 'discarded'; label: string }

export function changeStatusChip(
  change: ManufacturerDataChange,
  actorName?: (userId: string | null | undefined) => string | null,
): ChangeStatusChip | null {
  if (change.status === 'scheduled') {
    return { state: 'scheduled', label: change.effective_on ? `Takes effect ${calendarDay(change.effective_on)}` : 'Scheduled' }
  }
  if (change.status === 'held') return { state: 'held', label: 'Waiting on you' }
  if (change.status === 'discarded') return { state: 'discarded', label: 'Discarded' }
  if (change.status === 'reverted') {
    const by = actorName?.(change.reverted_by) ?? null
    const parts = [by ? `Reverted by ${by}` : 'Reverted', change.reverted_at ? shortDay(change.reverted_at) : null]
    return { state: 'reverted', label: parts.filter(Boolean).join(' · ') }
  }
  return null
}

/**
 * The open "price_sheet_review" item a held sheet raised: the one naming the change in its structured data,
 * else the one raised from the same email.
 */
export function heldChangeAction(
  change: ManufacturerDataChange,
  actions: readonly ManufacturerDataAction[],
): ManufacturerDataAction | null {
  const open = actions.filter(
    (action) => action.action_type === 'price_sheet_review' && (action.status === 'open' || action.status === 'in_progress'),
  )
  const named = open.find((action) => structuredChangeId(action.structured_data) === change.id)
  if (named) return named
  if (change.communication_message_id == null) return null
  return open.find((action) => action.communication_message_id === change.communication_message_id) ?? null
}

function structuredChangeId(raw: string | null | undefined): number | null {
  if (!raw) return null
  try {
    const value: unknown = JSON.parse(raw)
    if (value && typeof value === 'object' && 'change_id' in value) {
      const id = Number((value as { change_id: unknown }).change_id)
      return Number.isFinite(id) ? id : null
    }
  } catch {
    // Not JSON: nothing names the change.
  }
  return null
}

const ITEM_NOUNS: Record<string, string> = {
  price_sheet: 'price',
  discount: 'discount',
  special: 'special',
  lead_time: 'product',
  product_facts: 'fact',
  product_files: 'file',
  new_products: 'product',
  warehouses: 'warehouse',
  warranty: 'product',
}

/** "Show 412 prices": what a change's items are, counted. */
export function itemCountLabel(change: ManufacturerDataChange): string {
  return plural(change.item_count, ITEM_NOUNS[change.change_type] ?? 'item')
}

function itemValue(kind: string, value: string | null | undefined): string {
  if (value == null || value === '') {
    if (kind === 'special_net') return 'Not set'
    if (kind === 'discount') return 'No discount'
    return 'None'
  }
  if (kind === 'special_net' && Number.isFinite(Number(value))) return formatMoney(value)
  if (kind === 'lead_time') return days(value) ?? value
  return value
}

/** The unit a fact's bare number is in, read from its label ("TSSU-48 weight", "TSSU-48 height"). */
function factUnit(label: string | null | undefined): string | null {
  if (!label) return null
  if (/\bweight$/i.test(label)) return 'lb'
  if (/\b(height|width|depth)$/i.test(label)) return 'in'
  return null
}

function factValue(value: string | null | undefined, unit: string | null): string {
  if (value == null || value === '') return 'Not set'
  // Only a measurement is a number to tidy; anything else (a barcode) reads as given.
  if (!unit || value.trim() === '' || !Number.isFinite(Number(value))) return value
  return `${formatNumber(value)} ${unit}`
}

const WAREHOUSE_OFF = /^(inactive|off|turned off|false|disabled)$/i
const WAREHOUSE_ON = /^(active|on|true|enabled)$/i

/** What happened to a ship-from warehouse: added (or back on), turned off, or moved. */
function warehouseValue(item: ManufacturerDataChangeItem): string {
  const before = item.previous_value?.trim() || null
  const next = item.new_value?.trim() || null
  if (!next || WAREHOUSE_OFF.test(next)) return 'Turned off'
  const stateOnly = WAREHOUSE_ON.test(next) || (before != null && WAREHOUSE_ON.test(before))
  if (!before || WAREHOUSE_OFF.test(before) || stateOnly || before === next) return 'Added'
  return `${before} → ${next}`
}

/** "TSSU-48: spec-sheet.pdf" → model and file name; a label without the model is the file name alone. */
function splitFileLabel(label: string | null | undefined): { model: string | null; file: string } {
  const text = label?.trim() || ''
  const at = text.indexOf(': ')
  if (at <= 0) return { model: null, file: text || 'File' }
  return { model: text.slice(0, at), file: text.slice(at + 2) || 'File' }
}

/** What an item is about, shown first on its row: the model a file or fact belongs to, a warehouse's address. */
export function itemSubject(item: ManufacturerDataChangeItem): string {
  if (item.kind === 'image' || item.kind === 'document') return splitFileLabel(item.label).model ?? 'Unnamed'
  return item.label || 'Unnamed'
}

/**
 * "$412.00 → $389.50", "50/10 → 50/10/5", "80 lb → 84 lb", or the new value alone when there was none before.
 * A photo or spec sheet is its file name, a product its model, a warehouse what happened to it.
 */
export function describeItem(item: ManufacturerDataChangeItem): string {
  if (item.kind === 'image' || item.kind === 'document') return splitFileLabel(item.label).file
  if (item.kind === 'product') return item.label || 'Unnamed'
  if (item.kind === 'warehouse') return warehouseValue(item)
  if (item.kind === 'fact') {
    const unit = factUnit(item.label)
    const next = factValue(item.new_value, unit)
    if (item.previous_value == null || item.previous_value === '') return next
    return `${factValue(item.previous_value, unit)} → ${next}`
  }
  const next = itemValue(item.kind, item.new_value)
  // A price shows the NET it sold at before, whatever set it; other kinds show their earlier setting.
  const before = item.kind === 'special_net' && item.previous_net ? item.previous_net : item.previous_value
  if (before == null || before === '') return next
  return `${itemValue(item.kind, before)} → ${next}`
}

/** A row in "What the agent changed": one change, or one email's same lead time on several models. */
export type ChangeRow = { key: string; change: ManufacturerDataChange; scope: string | null; ids: number[] }

/**
 * Same-email, same-value, same-status product lead-time changes read as one row listing their models; pricing
 * and everything else stays one row per change.
 */
export function changeRows(changes: readonly ManufacturerDataChange[]): ChangeRow[] {
  const rows: ChangeRow[] = []
  let models: string[] = []
  for (const change of changes) {
    const last = rows.at(-1)
    const model =
      change.change_type === 'lead_time' && change.target.level === 'product' && typeof change.target.model === 'string'
        ? change.target.model
        : null
    if (
      model &&
      last &&
      last.change.target.level === 'product' &&
      last.change.communication_message_id === change.communication_message_id &&
      last.change.change_type === change.change_type &&
      last.change.new_value === change.new_value &&
      last.change.status === change.status
    ) {
      models.push(model)
      last.scope = models.join(', ')
      last.ids.push(change.id)
      continue
    }
    models = model ? [model] : []
    rows.push({ key: String(change.id), change, scope: changeScope(change.target), ids: [change.id] })
  }
  return rows
}
